import { useEffect, useState } from "react";
import { Archive, Check, Loader2, MessageCircleHeart, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const WORKED_OPTIONS = [
  { key: "yes", label: "Yes, regularly" },
  { key: "somewhat", label: "Somewhat" },
  { key: "not_yet", label: "Not yet, and that's okay" },
];

const STATUS_OPTIONS = [
  { key: "planning", label: "Planning" },
  { key: "exploring", label: "Exploring" },
  { key: "in_progress", label: "In Progress" },
  { key: "completed", label: "Completed" },
  { key: "paused", label: "Paused" },
];

const displayStatus = (s) => (s === "active" ? "in_progress" : s || "planning");
const statusLabel = (s) =>
  STATUS_OPTIONS.find((o) => o.key === displayStatus(s))?.label ||
  (s === "archived" ? "Retired" : s);

const isCurrent = (i) => !["archived", "completed"].includes(i.status);

const fmt = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export const IntentionsPanel = ({ entries }) => {
  const [intentions, setIntentions] = useState(null);
  const [newText, setNewText] = useState("");
  const [adding, setAdding] = useState(false);
  const [reflectingOn, setReflectingOn] = useState(null);
  const [workedOn, setWorkedOn] = useState("yes");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/intentions").then(({ data }) => setIntentions(data.intentions)).catch(() => setIntentions([]));

  useEffect(() => {
    load();
  }, []);

  const originDate = (assessmentId) => {
    const e = entries?.find((x) => x.assessment_id === assessmentId);
    return e ? fmt(e.created_at) : null;
  };

  const addIntention = async () => {
    if (newText.trim().length < 3) {
      toast.error("Write a few words for your intention first.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/intentions", { text: newText.trim() });
      setNewText("");
      setAdding(false);
      toast.success("Intention saved.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/intentions/${id}`, { status });
      toast.success(
        status === "completed"
          ? "Marked complete. Wonderful, however it unfolded."
          : "Status updated."
      );
      load();
    } catch {
      toast.error("Could not update that intention.");
    }
  };

  const saveReflection = async (id) => {
    setSaving(true);
    try {
      await api.post(`/intentions/${id}/reflections`, {
        worked_on: workedOn,
        note: note.trim() || null,
      });
      setReflectingOn(null);
      setNote("");
      toast.success("Reflection saved. No judgment, just notes for your journey.");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  if (!intentions) {
    return <Loader2 className="h-6 w-6 animate-spin text-forest" />;
  }

  const current = intentions.filter(isCurrent);
  const past = intentions.filter((i) => !isCurrent(i) || i.reflections.length > 0);

  return (
    <div
      data-testid="intentions-panel"
      className="rounded-[2rem] border border-forest/10 bg-white/75 p-7 shadow-[0_12px_30px_rgba(23,63,53,0.07)] sm:p-9"
    >
      <h3 className="font-serif text-xl text-forest">Your Wellness Intentions</h3>
      <p className="mt-2 text-xs leading-relaxed text-taupe">
        Personal focuses you have chosen over time. Always voluntary, never a scorecard, and
        completing one is never a medical claim.
      </p>

      {current.length === 0 && !adding && (
        <p className="mt-5 text-sm text-charcoal/65">
          No active intention right now. You can choose one after your next discovery, or write
          one here.
        </p>
      )}

      <div className="mt-5 flex flex-col gap-4">
        {current.map((i) => (
          <div
            key={i.intention_id}
            data-testid={`intention-card-${i.intention_id}`}
            className="rounded-2xl bg-forest p-6"
          >
            <p className="font-serif text-base italic leading-relaxed text-ivory">"{i.text}"</p>
            <p className="mt-2 text-[11px] uppercase tracking-[0.15em] text-ivory/50">
              Chosen {fmt(i.created_at)}
              {i.assessment_id && originDate(i.assessment_id) && (
                <> · from your {originDate(i.assessment_id)} discovery</>
              )}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  data-testid={`intention-status-${o.key}`}
                  onClick={() => setStatus(i.intention_id, o.key)}
                  className={`rounded-full border px-3.5 py-1.5 text-[11px] font-semibold transition-colors ${
                    displayStatus(i.status) === o.key
                      ? "border-gold-soft bg-gold/25 text-ivory"
                      : "border-ivory/20 text-ivory/60 hover:border-gold-soft/60 hover:text-ivory"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap gap-2 border-t border-ivory/10 pt-4">
              <button
                data-testid={`intention-reflect-${i.intention_id}`}
                onClick={() => setReflectingOn(reflectingOn === i.intention_id ? null : i.intention_id)}
                className="inline-flex items-center gap-1.5 rounded-full border border-ivory/30 px-4 py-2 text-xs font-semibold text-ivory transition-colors hover:border-gold-soft hover:text-gold-soft"
              >
                <MessageCircleHeart className="h-3.5 w-3.5" />
                Record a check-in
              </button>
              <button
                data-testid={`intention-archive-${i.intention_id}`}
                onClick={() => setStatus(i.intention_id, "archived")}
                className="inline-flex items-center gap-1.5 rounded-full border border-ivory/20 px-4 py-2 text-xs font-semibold text-ivory/70 transition-colors hover:border-gold-soft hover:text-gold-soft"
              >
                <Archive className="h-3.5 w-3.5" />
                Retire this intention
              </button>
            </div>
            {reflectingOn === i.intention_id && (
              <div className="mt-4 rounded-2xl bg-ivory/10 p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gold-soft">
                  Did you get to work on this?
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {WORKED_OPTIONS.map((o) => (
                    <button
                      key={o.key}
                      data-testid={`reflect-${o.key}`}
                      onClick={() => setWorkedOn(o.key)}
                      className={`rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                        workedOn === o.key
                          ? "border-gold-soft bg-gold/20 text-ivory"
                          : "border-ivory/25 text-ivory/70 hover:border-gold-soft/60"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
                <textarea
                  data-testid="reflection-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  maxLength={1000}
                  placeholder="What changed? Optional, and only you can see this."
                  className="mt-3 w-full rounded-2xl border border-ivory/25 bg-ivory/10 px-4 py-3 text-sm text-ivory placeholder:text-ivory/40 outline-none focus:border-gold-soft"
                />
                <button
                  data-testid="reflection-save"
                  onClick={() => saveReflection(i.intention_id)}
                  disabled={saving}
                  className="mt-3 inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-semibold text-forest-deep transition-colors hover:bg-gold-soft disabled:opacity-50"
                >
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                  Save check-in
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {adding ? (
        <div className="mt-5">
          <textarea
            data-testid="intention-new-input"
            value={newText}
            onChange={(e) => setNewText(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Over the next 90 days, I want to..."
            className="w-full rounded-2xl border border-forest/20 bg-white/70 px-5 py-4 text-sm outline-none focus:border-gold"
          />
          <div className="mt-3 flex gap-3">
            <button
              data-testid="intention-new-save"
              onClick={addIntention}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-semibold text-ivory hover:bg-forest-deep disabled:opacity-50"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Save intention
            </button>
            <button
              onClick={() => setAdding(false)}
              className="text-sm font-semibold text-taupe underline-offset-4 hover:underline"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          data-testid="intention-add-button"
          onClick={() => setAdding(true)}
          className="mt-5 inline-flex items-center gap-2 rounded-full border border-forest/25 px-5 py-2.5 text-sm font-semibold text-forest transition-colors hover:border-gold"
        >
          <Plus className="h-4 w-4" />
          Add an intention
        </button>
      )}

      {past.length > 0 && (
        <div className="mt-7 border-t border-forest/10 pt-5">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-taupe">
            Earlier intentions and check-ins
          </p>
          <div className="mt-4 flex flex-col gap-4">
            {past.map((i) => (
              <div key={i.intention_id} className="rounded-2xl border border-forest/10 p-5">
                <p className="font-serif text-sm italic text-forest">"{i.text}"</p>
                <p className="mt-1 text-[11px] text-taupe">
                  {fmt(i.created_at)} · {statusLabel(i.status)}
                  {i.assessment_id && originDate(i.assessment_id) && (
                    <> · from your {originDate(i.assessment_id)} discovery</>
                  )}
                </p>
                {i.reflections.length > 0 && (
                  <ul className="mt-3 flex flex-col gap-2">
                    {i.reflections.map((r, idx) => (
                      <li key={idx} className="text-xs leading-relaxed text-charcoal/70">
                        <span className="font-semibold text-forest">
                          {fmt(r.created_at)} ·{" "}
                          {WORKED_OPTIONS.find((o) => o.key === r.worked_on)?.label || r.worked_on}
                        </span>
                        {r.note && <span>: {r.note}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
